<template>
  <section class="create-modal-existing-companies q-mb-sm">
    <header class="existing-section-header row items-center text-14 text-secondary">
      <q-icon name="business" size="1.1rem" class="q-mr-xs" />

      <span>With an existing company</span>
    </header>

    <div class="companies-container bg-white light-bordered light-radius q-pa-sm">
      <q-list v-if="sortedCompanies.length > 0" class="companies-list clean-list">
        <q-item
          v-for="company in sortedCompanies"
          :key="company.company_id"
          clickable
          class="company-item light-bordered light-radius q-py-sm"
          :class="{ 'company-item--selected': company.company_id === selectedCompanyId }"
          @click="onRowClick(company)"
        >
          <q-item-section class="no-wrap">
            <div class="q-gutter-x-sm row items-center no-wrap">
              <q-icon
                :name="
                  company.company_id === selectedCompanyId
                    ? 'check_circle'
                    : 'radio_button_unchecked'
                "
                :class="company.company_id === selectedCompanyId ? 'text-brand' : 'text-secondary'"
                size="1.25rem"
                class="company-item__indicator"
              />

              <div class="col overflow-hidden">
                <p class="q-mb-none text-16 text-primary text-font-medium ellipsis">
                  {{ company.company_legal_name || company.company_name }}
                </p>

                <p
                  v-if="company.company_name && company.company_legal_name"
                  class="q-mb-none text-14 text-primary text-font-light ellipsis"
                >
                  {{ company.company_name }}
                </p>
              </div>

              <div
                v-if="company.score"
                class="score-badge flex column light-radius q-pa-xs text-center"
                :class="`bg-${getScoreColor(company.score)}`"
              >
                <span class="text-12 text-white text-font-medium">
                  {{ Math.round(company.score) }}%
                </span>

                <span class="text-white text-font-light"> match </span>
              </div>
            </div>
          </q-item-section>
        </q-item>
      </q-list>

      <p v-else class="empty-state q-py-md q-mb-none text-14 text-secondary text-center">
        No existing companies found.
      </p>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  companies: {
    type: Array,

    default: () => [],
  },

  selectedCompanyId: {
    type: String,

    default: null,
  },
})

const emit = defineEmits(['select-company'])

const sortedCompanies = computed(() =>
  [...props.companies].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)),
)

const getScoreColor = (score) => {
  if (score >= 85) return 'positive'

  if (score >= 70) return 'orange-7'

  return 'negative'
}

const onRowClick = (company) => {
  emit('select-company', company)
}
</script>

<style lang="scss" scoped>
@import '/src/css/setup/variables.scss';

.create-modal-existing-companies {
  display: flex;
  flex-direction: column;
}

.existing-section-header {
  gap: 0.25rem;
  margin-bottom: 0.25rem;
}

.companies-container {
  background-color: $clr-accent-table !important;
}

.companies-list {
  max-height: 190px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.company-item {
  min-height: 56px;
  cursor: pointer;
  background-color: white;

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:not(.company-item--selected):hover {
    background-color: $clr-accent-table;
  }

  &--selected {
    border: 2px solid $clr-brand !important;
  }
}

.company-item__indicator {
  flex-shrink: 0;
}

.score-badge {
  padding: 0.25rem 0.5rem;
  flex-shrink: 0;

  span {
    line-height: 0.6rem;

    &:last-of-type {
      font-size: 10px !important;
    }
  }
}

.empty-state {
  opacity: 0.85;
}
</style>
